import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/glucose";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Check, Sparkle, CreditCard, CurrencyInr, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

const FREE_FEATURES = [
  "7 days of history",
  "1 follower / caregiver",
  "Live chart + basic stats",
  "Manual entry & CGM uploader",
];

const PRO_FEATURES = [
  "Unlimited history retention",
  "Unlimited followers",
  "Full Reports (AGP, Distribution, Daily)",
  "Priority CGM uploader API",
  "CSV & Apple Health import / export",
  "Bolus calculator + prediction curve",
  "Dexcom Share auto-sync",
  "Early access to new features",
];

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// Pricing displayed to the user. Server enforces the actual amounts charged.
// Yearly plans display a monthly-equivalent price with a "billed annually" note.
const PRICES = {
  monthly: {
    USD: { label: "$5",    unit: "/mo", billedAs: null,          plan: "pro_monthly_usd" },
    INR: { label: "₹400",  unit: "/mo", billedAs: null,          plan: "pro_monthly_inr" },
  },
  yearly: {
    USD: { label: "$2.99", unit: "/mo", billedAs: "$35.88/yr",   plan: "pro_yearly_usd", monthly_equiv: 2.99, yearly_total: 35.88 },
    INR: { label: "₹249",  unit: "/mo", billedAs: "₹2,988/yr",   plan: "pro_yearly_inr", monthly_equiv: 249,  yearly_total: 2988 },
  },
};

const FREE_LABEL = { USD: "$0", INR: "₹0" };

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState("USD");
  const [billing, setBilling] = useState("yearly"); // default to the sweetest deal
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/subscription/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const isPro = status?.is_pro && !status?.expired;

  const price = PRICES[billing][currency];
  const unit = price.unit;

  // Compute % savings for the yearly plan vs paying monthly for a full year.
  const savings = useMemo(() => {
    if (billing !== "yearly") return null;
    const monthly = PRICES.monthly[currency];
    const monthlyRate = currency === "INR" ? 400 : 5;
    const monthlyOverYear = monthlyRate * 12;
    const yearlyTotal = price.yearly_total;
    if (!yearlyTotal || yearlyTotal >= monthlyOverYear) return null;
    return {
      pct: Math.round((1 - yearlyTotal / monthlyOverYear) * 100),
      monthlyOverYear,
      yearlyTotal,
    };
  }, [billing, currency, price]);

  const upgrade = async () => {
    setBusy(true);
    try {
      if (currency === "USD") {
        const r = await api.post("/payments/stripe/checkout", {
          plan_id: price.plan,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.checkout_url;
      } else {
        const ok = await loadRazorpayScript();
        if (!ok) { toast.error("Failed to load Razorpay"); return; }
        const order = await api.post("/payments/razorpay/order", { plan_id: price.plan });
        const options = {
          key: order.data.key_id,
          amount: order.data.amount,
          currency: order.data.currency,
          name: "GlucoCloud",
          description: `Pro — ${billing === "yearly" ? "1 year" : "1 month"}`,
          order_id: order.data.order_id,
          prefill: { name: user?.name || "", email: user?.email || "" },
          theme: { color: "#000000" },
          handler: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Welcome to Pro!");
              navigate("/payment/success?session_id=" + resp.razorpay_order_id);
            } catch { toast.error("Payment verification failed"); }
          },
          modal: { ondismiss: () => setBusy(false) },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-10" data-testid="pricing-root">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Plans</div>
        <h1 className="font-display font-light text-4xl sm:text-5xl tracking-[-0.02em]">Simple pricing</h1>
        <p className="text-muted-foreground mt-3">Two ways to pay. Cancel anytime.</p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <ToggleGroup type="single" value={billing} onValueChange={(v) => v && setBilling(v)} className="border border-border rounded-md" data-testid="billing-toggle">
            <ToggleGroupItem value="monthly" data-testid="billing-monthly" className="px-4 h-9 text-xs">
              Monthly
            </ToggleGroupItem>
            <ToggleGroupItem value="yearly" data-testid="billing-yearly" className="px-4 h-9 text-xs gap-1.5">
              Yearly {billing === "yearly" && savings && <span className="text-[9px] px-1.5 py-0.5 rounded-full sig-in-range bg-sig-in-range/10">Save {savings.pct}%</span>}
              {billing !== "yearly" && <span className="text-[9px] px-1.5 py-0.5 rounded-full sig-in-range bg-sig-in-range/10">Save 40%</span>}
            </ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup type="single" value={currency} onValueChange={(v) => v && setCurrency(v)} className="border border-border rounded-md" data-testid="currency-toggle">
            <ToggleGroupItem value="USD" data-testid="currency-usd" className="px-4 h-9 text-xs gap-1.5">
              <CurrencyDollar size={14} /> USD
            </ToggleGroupItem>
            <ToggleGroupItem value="INR" data-testid="currency-inr" className="px-4 h-9 text-xs gap-1.5">
              <CurrencyInr size={14} /> INR
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free */}
        <div className="p-6 sm:p-8 rounded-lg border border-border bg-card" data-testid="plan-free">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Free</div>
          <div className="font-display font-light text-4xl sm:text-5xl tabular">{FREE_LABEL[currency]}<span className="text-base text-muted-foreground">/mo</span></div>
          <p className="text-sm text-muted-foreground mt-2">Try GlucoCloud for a week. All the basics with a rolling 7-day window.</p>
          <ul className="space-y-3 mt-8">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check size={16} className="mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <Button variant="outline" className="w-full mt-8" disabled data-testid="free-current-btn">Current plan</Button>
        </div>

        {/* Pro */}
        <div className="p-6 sm:p-8 rounded-lg border-2 border-foreground bg-card relative" data-testid="plan-pro">
          <div className="absolute -top-3 left-6 px-2.5 py-0.5 bg-foreground text-background text-[10px] uppercase tracking-[0.18em] rounded flex items-center gap-1">
            <Sparkle size={10} weight="fill" /> Recommended
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Pro · {billing}</div>
          <div className="font-display font-light text-4xl sm:text-5xl tabular" data-testid="pro-price">
            {price.label}<span className="text-base text-muted-foreground">{unit}</span>
          </div>
          {price.billedAs && (
            <div className="text-xs text-muted-foreground mt-1" data-testid="pro-billed-as">
              Billed as {price.billedAs}
              {savings && <> · <span className="sig-in-range font-medium">Save {savings.pct}%</span></>}
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-3">
            Unlimited history &amp; reports for serious CGM users.
            {billing === "yearly" ? " One payment per year." : " Billed monthly."}
          </p>
          <ul className="space-y-3 mt-8">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check size={16} weight="bold" className="mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
          {isPro ? (
            <Button className="w-full mt-8" disabled data-testid="pro-current-btn">You&apos;re on Pro ✓</Button>
          ) : (
            <Button
              className="w-full mt-8 bg-foreground text-background hover:bg-foreground/90 gap-2"
              onClick={upgrade}
              disabled={busy}
              data-testid="upgrade-btn"
            >
              <CreditCard size={16} /> {busy ? "Redirecting…" : `Upgrade — ${price.billedAs || `${price.label}${unit}`}`}
            </Button>
          )}
          {status?.expired && (
            <div className="text-xs text-center text-muted-foreground mt-3">Your previous Pro plan expired {new Date(status.expires_at).toLocaleDateString()}.</div>
          )}
        </div>
      </div>
    </div>
  );
}
